import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES, STATUSES, fmtMoney } from "@/lib/leadMeta";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AddLeadDialog } from "@/components/AddLeadDialog";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import { CommissionStatement } from "@/components/CommissionStatement";
import { Users, DollarSign, Target, Inbox, Bell, BellRing, BellOff, CalendarDays, ListTodo, Clock, MapPin, User as UserIcon, CalendarPlus, Percent, Flame } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { googleCalendarUrl } from "@/lib/gcal";
import { APPT_KINDS } from "@/lib/apptKinds";

const isSameDay = (iso) => {
  if (!iso) return false;
  const d = new Date(iso); const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
};
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
const PRIO_COLOR = { high: "#ef4444", normal: "#0ea5e9", low: "#64748b" };

function TodayPanel() {
  const navigate = useNavigate();
  const [appts, setAppts] = useState([]);
  const [todos, setTodos] = useState([]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    api.get("/appointments").then(({ data }) => setAppts(data)).catch(() => {});
    api.get("/todos").then(({ data }) => setTodos(data)).catch(() => {});
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  const todayAppts = appts.filter((a) => isSameDay(a.start)).sort((a, b) => (a.start > b.start ? 1 : -1));
  const nextUp = appts
    .map((a) => ({ a, t: new Date(a.start_at || a.start).getTime() }))
    .filter((x) => !isNaN(x.t) && x.t > now)
    .sort((x, y) => x.t - y.t)[0];
  const countdown = (ms) => {
    const m = Math.round(ms / 60000);
    if (m < 60) return `in ${m} min`;
    const h = Math.floor(m / 60), mm = m % 60;
    if (h < 24) return `in ${h}h${mm ? ` ${mm}m` : ""}`;
    const days = Math.round(h / 24);
    return `in ${days} day${days !== 1 ? "s" : ""}`;
  };
  const openTodos = todos.filter((t) => !t.done).sort((a, b) => {
    const rank = { high: 0, normal: 1, low: 2 };
    return (rank[a.priority] ?? 1) - (rank[b.priority] ?? 1) || (a.due_date || "9999").localeCompare(b.due_date || "9999");
  }).slice(0, 5);

  return (
    <div className="space-y-6">
      {nextUp && (
        <button onClick={() => navigate("/app/planner")} data-testid="next-up-banner"
          className="flex w-full items-center gap-3 rounded-xl border border-primary/30 bg-primary/10 px-5 py-3 text-left transition-colors hover:bg-primary/15">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/20"><Clock className="h-4 w-4 text-primary" /></span>
          <span className="min-w-0">
            <span className="text-xs font-medium uppercase tracking-wide text-primary">Next up</span>
            <span className="block truncate text-sm font-semibold">{nextUp.a.title}{nextUp.a.lead_name ? ` · ${nextUp.a.lead_name}` : ""} <span className="font-normal text-muted-foreground">— {countdown(nextUp.t - now)} · {fmtTime(nextUp.a.start)}</span></span>
          </span>
        </button>
      )}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2" data-testid="today-widget">
      <Card className="p-6" data-testid="today-appointments">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-heading text-lg font-semibold"><CalendarDays className="h-5 w-5 text-primary" /> Today's appointments</h3>
          <button onClick={() => navigate("/app/planner")} className="text-xs font-medium text-primary hover:underline" data-testid="today-open-planner">Open Planner</button>
        </div>
        <div className="mt-4 space-y-2">
          {todayAppts.length === 0 && <p className="rounded-lg border border-dashed border-border p-5 text-center text-sm text-muted-foreground" data-testid="today-appts-empty">No appointments today. Enjoy the open calendar.</p>}
          {todayAppts.map((a) => (
            <div key={a.appointment_id} className="flex items-center justify-between gap-3 rounded-lg border border-border p-3" data-testid={`today-appt-${a.appointment_id}`}>
              <div className="min-w-0">
                <p className="flex items-center gap-2 truncate font-medium"><span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: (APPT_KINDS[a.kind] || APPT_KINDS.call).color }} />{a.title}</p>
                <p className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtTime(a.start)}{a.end ? ` – ${fmtTime(a.end)}` : ""}</span>
                  {a.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {a.location}</span>}
                  {a.lead_name && <span className="flex items-center gap-1 text-primary"><UserIcon className="h-3 w-3" /> {a.lead_name}</span>}
                </p>
              </div>
              {googleCalendarUrl(a) && (
                <a href={googleCalendarUrl(a)} target="_blank" rel="noopener noreferrer" title="Add to Google Calendar"
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
                  <CalendarPlus className="h-4 w-4" />
                </a>
              )}
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6" data-testid="today-todos">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-heading text-lg font-semibold"><ListTodo className="h-5 w-5 text-primary" /> Top to-dos</h3>
          <button onClick={() => navigate("/app/planner")} className="text-xs font-medium text-primary hover:underline" data-testid="today-open-todos">View all</button>
        </div>
        <div className="mt-4 space-y-2">
          {openTodos.length === 0 && <p className="rounded-lg border border-dashed border-border p-5 text-center text-sm text-muted-foreground" data-testid="today-todos-empty">You're all caught up. Nice work.</p>}
          {openTodos.map((t) => (
            <div key={t.todo_id} className="flex items-center gap-3 rounded-lg border border-border p-3" data-testid={`today-todo-${t.todo_id}`}>
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: PRIO_COLOR[t.priority] || PRIO_COLOR.normal }} />
              <p className="min-w-0 flex-1 truncate text-sm font-medium">{t.title}</p>
              {t.due_date && <span className="shrink-0 text-xs text-muted-foreground">Due {new Date(t.due_date + "T00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
            </div>
          ))}
        </div>
      </Card>
      </div>
    </div>
  );
}

const SNOOZE_KEY = "newLeadsSnoozeUntil";

function NewLeadsButton() {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [snoozeUntil, setSnoozeUntil] = useState(() => Number(localStorage.getItem(SNOOZE_KEY)) || 0);
  const snoozed = snoozeUntil > Date.now();
  useEffect(() => {
    const load = () => api.get("/new-leads").then(({ data }) => setCount(data.count || 0)).catch(() => {});
    load();
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (!snoozed) return undefined;
    const t = setTimeout(() => setSnoozeUntil(0), Math.max(0, snoozeUntil - Date.now()));
    return () => clearTimeout(t);
  }, [snoozed, snoozeUntil]);
  const snooze = (mins) => { const until = Date.now() + mins * 60000; localStorage.setItem(SNOOZE_KEY, String(until)); setSnoozeUntil(until); };
  const resume = () => { localStorage.removeItem(SNOOZE_KEY); setSnoozeUntil(0); };
  const open = async () => { try { await api.post("/new-leads/ack"); } catch { /* ignore */ } setCount(0); navigate("/app/leads"); };

  if (snoozed) {
    return (
      <button onClick={resume} data-testid="new-leads-snoozed" title="Resume New Leads alerts"
        className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
        <BellOff className="h-4 w-4" /> Alerts snoozed · Resume
      </button>
    );
  }
  if (count > 0) {
    return (
      <div className="flex items-center gap-2">
        <button onClick={open} data-testid="new-leads-button"
          className="inline-flex animate-pulse items-center gap-2 rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-lg ring-2 ring-red-400/60 transition-transform hover:scale-105">
          <BellRing className="h-4 w-4" /> New Leads ({count})
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="snooze-trigger" title="Snooze alerts" className="inline-flex items-center rounded-full border border-border p-2 text-muted-foreground transition-colors hover:text-foreground"><BellOff className="h-4 w-4" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => snooze(30)} data-testid="snooze-30">Snooze 30 minutes</DropdownMenuItem>
            <DropdownMenuItem onClick={() => snooze(60)} data-testid="snooze-60">Snooze 1 hour</DropdownMenuItem>
            <DropdownMenuItem onClick={() => snooze(120)} data-testid="snooze-120">Snooze 2 hours</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }
  return (
    <span data-testid="no-new-leads" className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium text-muted-foreground">
      <Bell className="h-4 w-4" /> No New Leads
    </span>
  );
}

const StatCard = ({ icon: Icon, label, value, sub, tid }) => (
  <Card className="p-5" data-testid={tid}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      <Icon className="h-4 w-4 text-primary" />
    </div>
    <p className="mt-3 font-heading text-3xl font-bold">{value}</p>
    {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
  </Card>
);

export default function Overview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/stats/overview").then(({ data }) => setStats(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!stats) {
    return (
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
      </div>
    );
  }

  const maxType = Math.max(...Object.values(stats.by_type), 1);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold sm:text-3xl">Welcome back, {user.name.split(" ")[0]}</h1>
          <p className="mt-1 text-sm text-muted-foreground">Here's how your pipeline looks today.</p>
        </div>
        <div className="flex items-center gap-3">
          <NewLeadsButton />
          <AddLeadDialog onDone={load} />
        </div>
      </div>

      <OnboardingChecklist onAddLead={() => setAddOpen(true)} />
      <AddLeadDialog onDone={load} open={addOpen} onOpenChange={setAddOpen} hideTrigger />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <StatCard icon={Users} label="Total Leads" value={stats.total_leads} sub={`${stats.unassigned} unassigned`} tid="stat-total-leads" />
        <StatCard icon={DollarSign} label="Pipeline Premium" value={fmtMoney(stats.total_premium)} sub="Estimated annual" tid="stat-premium" />
        <StatCard icon={Percent} label="Proj. Commission" value={fmtMoney(stats.total_commission)} sub="Across pipeline" tid="stat-commission" />
        <StatCard icon={Target} label="Conversion" value={`${stats.conversion_rate}%`} sub="Won / closed" tid="stat-conversion" />
        <StatCard icon={Inbox} label="Won Deals" value={stats.by_status.closed_won} sub="Closed won" tid="stat-won" />
      </div>

      <Card className="flex flex-col items-start justify-between gap-3 border-primary/30 bg-gradient-to-r from-primary/10 to-transparent p-5 sm:flex-row sm:items-center" data-testid="forecast-tile">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary"><Flame className="h-5 w-5" /></span>
          <div>
            <p className="text-sm font-semibold">Projected commission this month</p>
            <p className="text-xs text-muted-foreground">From near-term deals — Hot leads and those you've already contacted</p>
          </div>
        </div>
        <span className="font-heading text-3xl font-bold text-primary" data-testid="forecast-amount">{fmtMoney(stats.forecast_commission)}</span>
      </Card>

      <CommissionStatement />

      <TodayPanel />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="p-6" data-testid="chart-by-type">
          <h3 className="font-heading text-lg font-semibold">Leads by product line</h3>
          <div className="mt-5 space-y-4">
            {Object.entries(stats.by_type).map(([k, v]) => {
              const cat = CATEGORIES[k];
              return (
                <div key={k}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2"><cat.icon className="h-4 w-4" style={{ color: cat.color }} /> {cat.label}</span>
                    <span className="font-mono font-medium">{v}</span>
                  </div>
                  <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-muted">
                    <div className="h-full rounded-full transition-all" style={{ width: `${(v / maxType) * 100}%`, backgroundColor: cat.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="p-6" data-testid="chart-by-status">
          <h3 className="font-heading text-lg font-semibold">Pipeline by status</h3>
          <div className="mt-5 space-y-3">
            {Object.entries(stats.by_status).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between rounded-lg border border-border px-4 py-2.5">
                <span className="flex items-center gap-2 text-sm">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: STATUSES[k].color }} /> {STATUSES[k].label}
                </span>
                <span className="font-mono text-sm font-semibold">{v}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
